import { useState, useEffect, useRef } from 'react';

export default function Solution() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="solution" className="py-20 bg-white relative overflow-hidden">
    {/* Background Elements */}
    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#2a5bd7]/10 to-[#06b6d4]/10 rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-[#2ecc71]/10 to-[#06b6d4]/10 rounded-full blur-3xl"></div>
    
      <div className="container mx-auto px-6 relative z-10">
      <div className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        <div className="inline-flex items-center bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-3 rounded-full mb-6 border border-[#06b6d4]/20">
          <i className="ri-lightbulb-flash-line text-[#2a5bd7] mr-2"></i>
          <span className="text-[#3d5a80] font-semibold text-sm">Giải pháp toàn diện</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-black text-[#2a4b7c] mb-6">
          Stackway Skill Hub<br/>
          <span className="text-[#3d5a80]">
            giải quyết điều gì?
          </span>
        </h2>
        <p className="text-xl text-slate-600 leading-relaxed">
          Biến dữ liệu kỹ năng rời rạc thành hệ thống <span className="font-bold text-[#2a4b7c]">Skill Intelligence</span> thống nhất
        </p>
      </div>

      {/* Visual Flow Diagram */}
      <div className="max-w-6xl mx-auto">
        {/* Central Hub */}
        <div className="relative">
          {/* Center Circle - Main Platform */}
          <div className={`flex justify-center mb-16 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}>
            <div className="relative">
              <div className="w-64 h-64 bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-full flex items-center justify-center shadow-2xl">
                <div className="text-center text-white p-8">
                  <i className="ri-brain-line text-5xl mb-3"></i>
                  <h3 className="font-black text-xl mb-2">Skill Intelligence</h3>
                  <p className="text-sm opacity-90">Nền tảng trung tâm</p>
                </div>
              </div>
              {/* Pulse Effect */}
              <div className="absolute inset-0 rounded-full bg-[#2a5bd7]/30 animate-ping opacity-20"></div>
            </div>
          </div>

          {/* Connecting Lines & Feature Nodes */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
            {/* Feature 1 - Chuẩn hóa */}
            <div className={`relative group transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="absolute -top-16 left-1/2 w-0.5 h-16 bg-[#06b6d4]/40 hidden lg:block"></div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#2a4b7c]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-settings-3-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-full flex items-center justify-center mr-3">
                    <span className="text-[#2a4b7c] font-black text-sm">1</span>
                  </div>
                  <h3 className="font-bold text-lg text-[#2a4b7c]">Chuẩn hóa</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Xây dựng <strong className="text-[#2a4b7c]">skill framework & taxonomy</strong> thống nhất cho toàn tổ chức
                </p>
              </div>
            </div>

            {/* Feature 2 - Đánh giá */}
            <div className={`relative group transition-all duration-1000 delay-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="absolute -top-16 left-1/2 w-0.5 h-16 bg-[#06b6d4]/40 hidden lg:block"></div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#2a5bd7]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2a5bd7] to-[#06b6d4] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-full flex items-center justify-center mr-3">
                    <span className="text-[#2a5bd7] font-black text-sm">2</span>
                  </div>
                  <h3 className="font-bold text-lg text-[#2a4b7c]">Đánh giá</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                 Đánh giá năng lực <strong className="text-[#2a5bd7]">khách quan, có kiểm chứng</strong> theo chuẩn doanh nghiệp hoặc quốc tế
                </p>
              </div>
            </div>

            {/* Feature 3 - Phân tích */}
            <div className={`relative group transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="absolute -top-16 left-1/2 w-0.5 h-16 bg-[#06b6d4]/40 hidden lg:block"></div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#06b6d4]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-[#06b6d4] to-[#2ecc71] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-bar-chart-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-cyan-50 to-green-50 rounded-full flex items-center justify-center mr-3">
                    <span className="text-[#06b6d4] font-black text-sm">3</span>
                  </div>
                  <h3 className="font-bold text-lg text-[#2a4b7c]">Phân tích</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                 Phân tích khoảng cách kỹ năng <strong className="text-[#06b6d4]">cá nhân – Team – toàn doanh nghiệp</strong>
                </p>
              </div>
            </div>

            {/* Feature 4 - Đào tạo */}
            <div className={`relative group lg:col-start-1 transition-all duration-1000 delay-800 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#2ecc71]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2ecc71] to-[#06b6d4] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-lightbulb-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-50 to-cyan-50 rounded-full flex items-center justify-center mr-3">
                    <span className="text-[#2ecc71] font-black text-sm">4</span>
                  </div>
                  <h3 className="font-bold text-lg text-[#2a4b7c]">Đào tạo</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Gợi ý <strong className="text-[#2ecc71]">lộ trình phát triển</strong> và khóa học phù hợp với từng cá nhân
                </p>
              </div>
            </div>

            {/* Feature 5 - Hoạch định */}
            <div className={`relative group lg:col-start-3 transition-all duration-1000 delay-900 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#3d5a80]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-[#3d5a80] to-[#2a5bd7] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full flex items-center justify-center mr-3">
                    <span className="text-[#3d5a80] font-black text-sm">5</span>
                  </div>
                  <h3 className="font-bold text-lg text-[#2a4b7c]">Hoạch định</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Hỗ trợ <strong className="text-[#3d5a80]">chiến lược nguồn nhân lực</strong> dài hạn dựa trên dữ liệu
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Result Banner */}
          <div className={`mt-16 max-w-3xl mx-auto transition-all duration-1000 delay-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-2xl p-8 text-center text-white shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-center mb-4">
                  <i className="ri-trophy-line text-4xl mr-3"></i>
                  <h3 className="text-2xl font-black">Kết quả</h3>
                </div>
                <p className="text-xl font-semibold mb-2">
                Quyết định nhân sự dựa trên dữ liệu kỹ năng
                </p>
                <p className="text-lg opacity-90">
                Năng lực không còn là cảm nhận, mà là lợi thế chiến lược
                </p>
              </div>
              {/* Decorative Elements */}
              <div className="absolute top-4 right-4 w-20 h-20 border-2 border-white/20 rounded-full"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 border-2 border-white/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
}
